var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingButton, loadingButtonClasses } from '@mui/lab';
import { Box, Stack, Typography, styled, useTheme } from '@mui/material';
import { useContext } from 'react';
import { useTerminologyTranslation } from '../../app/terminology';
import { WORK_TYPE_ICON, WizardStep } from '../constants';
import { WizardContext } from '../context';
import { useGroupedQueues, useQueueIncomplete, useQueuesAPI } from '../queue/selectors';
import { useWizardParams } from '../selectors';
var StyledLoadingButton = styled(LoadingButton)(function (_a) {
  var _b;
  var theme = _a.theme;
  return _b = {}, _b["&.".concat(loadingButtonClasses.root)] = {
    justifyContent: 'flex-start !important',
    gap: theme.spacing(3),
    ':disabled': {
      color: "".concat(theme.palette.text.primary, " !important"),
      backgroundColor: 'transparent',
      borderColor: "".concat(theme.palette.primary.main, " !important")
    }
  }, _b;
});
var StyledStack = styled(Stack)(function (_a) {
  var _b;
  var theme = _a.theme;
  return _b = {
    maxHeight: theme.typography.pxToRem(400)
  }, _b["& button"] = {
    marginBottom: theme.typography.pxToRem(8)
  }, _b["& button:last-child"] = {
    marginBottom: theme.typography.pxToRem(20)
  }, _b;
});
var hasIncompleteWapVolumes = function (value) {
  if (Array.isArray(value)) return value.some(hasIncompleteWapVolumes);
  if (!value || typeof value !== 'object') return false;
  if ('workVolume' in value) {
    var workVolume = value.workVolume;
    return workVolume == null || workVolume === '';
  }
  return Object.values(value).some(hasIncompleteWapVolumes);
};
var QueueButton = function (_a) {
  var queue = _a.queue;
  var theme = useTheme();
  var _b = useWizardParams(),
    queueId = _b.queueId,
    step = _b.step;
  var incomplete = useQueueIncomplete(queue.id, step);
  var _c = useContext(WizardContext),
    handleSave = _c.handleSave,
    handleSaveWithoutValidation = _c.handleSaveWithoutValidation,
    actionRef = _c.actionRef,
    requesting = _c.requesting,
    goToQueue = _c.goToQueue,
    _d = _c.methods,
    getValues = _d.getValues,
    isDirty = _d.formState.isDirty;
  var handleClick = function () {
    var callback = function () {
      return goToQueue(queue.id);
    };
    var shouldSkipWapValidation = [WizardStep.WorkVolume, WizardStep.ProdHours].includes(step) && hasIncompleteWapVolumes(getValues('wapVolumes'));
    var shouldSkipValidation = isDirty && ([WizardStep.HandleTime, WizardStep.Attrition, WizardStep.WorkingDays, WizardStep.Shrinkage, WizardStep.Financial].includes(step) || shouldSkipWapValidation);
    if (shouldSkipValidation) {
      handleSaveWithoutValidation('switchQueue', callback);
      return;
    }
    handleSave('switchQueue', callback)();
  };
  return _jsxs(StyledLoadingButton, __assign({
    variant: "outlined",
    fullWidth: true,
    disabled: queue.id === queueId,
    loading: actionRef.current === 'switchQueue' && requesting && queue.id === queueId,
    onClick: handleClick
  }, {
    children: [_jsx(Typography, __assign({
      variant: "h3"
    }, {
      children: _jsx(FontAwesomeIcon, {
        icon: WORK_TYPE_ICON[queue.worktype.id - 1]
      })
    })), _jsxs(Box, __assign({
      textAlign: "left"
    }, {
      children: [_jsx(Typography, __assign({
        fontWeight: "bold"
      }, {
        children: queue.businessArea
      })), _jsx(Typography, __assign({
        variant: "body2"
      }, {
        children: queue.worktype.name
      }))]
    })), incomplete && _jsx(Typography, __assign({
      ml: "auto",
      color: theme.palette.warning.main
    }, {
      children: _jsx(FontAwesomeIcon, {
        icon: "triangle-exclamation"
      })
    }))]
  }));
};
export var QueuePanel = function () {
  var _a;
  var t = useTerminologyTranslation('wizard', {
    keyPrefix: 'queue'
  }).t;
  var queuesData = useQueuesAPI()[0];
  var groupedQueues = (_a = useGroupedQueues(queuesData === null || queuesData === void 0 ? void 0 : queuesData.data)) !== null && _a !== void 0 ? _a : [];
  return _jsxs(_Fragment, {
    children: [_jsx(Typography, __assign({
      variant: "h3",
      gutterBottom: true
    }, {
      children: t('name')
    })), _jsx(Typography, __assign({
      mb: 5
    }, {
      children: t('queuePanel.description')
    })), _jsx(StyledStack, {
      children: groupedQueues.map(function (group) {
        return _jsxs(Stack, __assign({
          direction: "column",
          spacing: 2
        }, {
          children: [_jsx(Typography, __assign({
            variant: "subtitle2",
            fontWeight: "bold"
          }, {
            children: group.label
          })), group.queues.map(function (queue) {
            return _jsx(QueueButton, {
              queue: queue
            }, queue.id);
          })]
        }), group.label);
      })
    })]
  });
};